import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
  useIsMobile,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { TargetTypeEnum } from '@ais-ui/websockets';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { PiAnchor, PiBoat } from 'react-icons/pi';
import { XIcon } from 'lucide-react';

/**
 * A 3D flip toggle button that rotates to reveal the alternate icon.
 * Used for switching between vessel and port search modes.
 */
interface FlipToggleButtonProps {
  isFlipped: boolean;
  onClick: (e: React.MouseEvent) => void;
  id: string;
  frontIcon: React.ReactNode;
  backIcon: React.ReactNode;
}

const FlipToggleButton: React.FC<FlipToggleButtonProps> = ({
  isFlipped,
  onClick,
  id,
  frontIcon,
  backIcon,
}) => {
  return (
    <Button
      id={id}
      variant="ghost"
      size="icon"
      className="h-6 w-6 [perspective:400px]"
      onClick={onClick}
    >
      <div
        className={cn(
          'relative h-4 w-4 transition-transform duration-500 [transform-style:preserve-3d]',
          'ease-[cubic-bezier(0.34,1.56,0.64,1)]',
          isFlipped && '[transform:rotateY(180deg)]',
        )}
      >
        {/* Front face - vessel icon */}
        <div className="absolute inset-0 flex items-center justify-center [backface-visibility:hidden]">
          {frontIcon}
        </div>
        {/* Back face - port icon (pre-rotated 180deg so it's right-way-up when flipped) */}
        <div className="absolute inset-0 flex items-center justify-center [backface-visibility:hidden] [transform:rotateY(180deg)]">
          {backIcon}
        </div>
      </div>
    </Button>
  );
};
import { useAISMapContext } from '../AISMapContext';
import { useLocalization } from '@ais-ui/locale-management';
import { AISShipSearch } from './AISShipSearch';
import { ShipSearchResult } from './useShipSearch';
import { useFetchShipByMmsi } from '@ais-ui/api';
import { PortSearch } from './PortSearch';
import { useSelectionState } from '../../../components/SelectionStateProvider';
import { usePermissions } from '../../Permissions/PermissionsProvider';

interface ShipSearchInputProps {
  className?: string;
  setSearchString: React.Dispatch<React.SetStateAction<string>>;
  searchString: string;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  inputRef: React.RefObject<HTMLInputElement>;
}

export const ShipSearchInput: React.FC<ShipSearchInputProps> = ({
  className,
  setSearchString,
  searchString,
  setOpen,
  inputRef,
}) => {
  const { messages } = useLocalization();
  const isMobile = useIsMobile();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value);
    if (searchString.length > 0) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const handleOnFocus = () => {
    setOpen(true);
  };

  const handleOnBlur = () => {
    if (searchString.length === 0) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (searchString.length > 0) {
      setOpen(true);
    }
  }, [searchString, setOpen]);

  const onClearSearch = () => {
    setSearchString('');
    setOpen(false);
  };

  return (
    <PopoverTrigger asChild>
      <div className={cn('relative w-80', className)}>
        <div className="absolute left-3 top-0 h-9 flex items-center">
          <FaSearch className="text-muted-foreground size-4" />
        </div>
        <Input
          ref={inputRef}
          className="pl-10 pr-20 border-none focus-visible:ring-0 shadow-md"
          placeholder={messages.shipSearchText}
          value={searchString}
          onChange={handleSearchChange}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          autoFocus
        />
        <div className="absolute right-2 top-0 h-9 flex items-center gap-1">
          {searchString.length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={onClearSearch}
                >
                  <XIcon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                {messages.shipSearchClearSearchTooltip}
              </TooltipContent>
            </Tooltip>
          )}
          {!isMobile && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center justify-center h-6 w-6">
                  <IoMdInformationCircleOutline className="text-primary size-4" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="right">
                {messages.shipSearchTooltip}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </PopoverTrigger>
  );
};

interface ShipPortSearchInputProps {
  className?: string;
  setSearchString: React.Dispatch<React.SetStateAction<string>>;
  searchString: string;
  setIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
  inputRef: React.RefObject<HTMLInputElement | null>;
  searchType: string;
  setSearchType: React.Dispatch<React.SetStateAction<'ship' | 'port'>>;
}

export const ShipPortSearchInput: React.FC<ShipPortSearchInputProps> = ({
  className,
  setSearchString,
  searchString,
  setIsFocused,
  inputRef,
  searchType,
  setSearchType,
}) => {
  const { messages } = useLocalization();
  const isMobile = useIsMobile();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchString(newValue);
  };
  const handleOnFocus = () => {
    setIsFocused(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const onClearSearch = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering popover toggle
    setSearchString('');
    setIsFocused(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const onChangeSearchType = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent triggering popover toggle

      setIsFocused(true);

      setSearchType((prevType) => (prevType === 'ship' ? 'port' : 'ship'));
      setSearchString('');
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
    [setIsFocused, setSearchType, setSearchString, inputRef],
  );

  const handleContainerClick = () => {
    setIsFocused(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <PopoverTrigger className={'popover-trigger'} asChild>
      <div
        className={cn('relative w-80', className)}
        onClick={handleContainerClick}
      >
        <div className="absolute left-3 top-0 h-9 flex items-center">
          <FaSearch className="text-muted-foreground size-4" />
        </div>
        <Input
          ref={inputRef}
          className="pl-10 pr-24 border-none focus-visible:ring-0 bg-background"
          placeholder={
            searchType === 'ship'
              ? messages.shipSearchText
              : messages.portSearchText
          }
          value={searchString}
          onChange={handleSearchChange}
          onFocus={handleOnFocus}
        />
        <div className="absolute right-2 top-0 h-9 flex items-center gap-1">
          {searchString.length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={onClearSearch}
                >
                  <XIcon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                {messages.mapShipPortSearchClearSearchTooltip}
              </TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <FlipToggleButton
                id={
                  searchType === 'ship'
                    ? 'change-to-port-search-button'
                    : 'change-to-ship-search-button'
                }
                isFlipped={searchType === 'port'}
                onClick={onChangeSearchType}
                frontIcon={<PiBoat className="size-4" />}
                backIcon={<PiAnchor className="size-4" />}
              />
            </TooltipTrigger>
            <TooltipContent side="right">
              {searchType === 'ship'
                ? messages.shipSearchChangeToPortSearch
                : messages.shipSearchChangeToVesselSearch}
            </TooltipContent>
          </Tooltip>
          {!isMobile && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center justify-center h-6 w-6">
                  <IoMdInformationCircleOutline className="text-primary size-4" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="right">
                {searchType === 'ship'
                  ? messages.shipSearchTooltip
                  : messages.portSearchTooltip}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </PopoverTrigger>
  );
};

interface ShipSearchProps {
  className?: string;
}

export const ShipPortSearch: React.FC<ShipSearchProps> = React.memo(
  ({ className }) => {
    const [searchString, setSearchString] = useState('');
    const [searchType, setSearchType] = useState<'ship' | 'port'>('ship');
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const { messages } = useLocalization();
    const permissions = usePermissions();
    const isMobile = useIsMobile();

    const hasShipDbPermissions =
      permissions.mdmAdvancedVesselInfo ||
      permissions.mdmRegister ||
      permissions.mdmIntegrity;

    const { setTargetInfoDetailsList } = useSelectionState();
    const { setZoomToShip } = useAISMapContext();
    const { toast } = useToast();

    const fetchShipByMmsi = useFetchShipByMmsi();

    const onSearchShipDbByMmsi = useCallback(
      async (mmsi: number) => {
        try {
          const ship = await fetchShipByMmsi(mmsi);
          if (!ship) {
            toast({
              className: 'extended-vessel-info',
              title: 'Not Found!',
              description: messages.mapShipPortSearchNotFoundInEviDescription,
              variant: 'destructive',
            });
            setIsFocused(false);
            return;
          }

          // On mobile: only zoom, don't select vessel
          if (!isMobile) {
            setTargetInfoDetailsList([
              {
                extractedTargetInfo: [ship],
                extractedTargetInfoType: TargetTypeEnum.ExtendedVessel,
              },
            ]);
          }
          setIsFocused(false);
        } catch (error) {
          toast({
            title: 'Error!',
            description: messages.mapShipPortSearchNotFoundInEviError,
            variant: 'destructive',
          });
          setIsFocused(false);
        }
      },
      [fetchShipByMmsi, setTargetInfoDetailsList, toast, isMobile],
    );

    const onAISShipClick = useCallback(
      (ship: ShipSearchResult) => {
        if (ship.name) {
          setSearchString(ship.name);
        }

        if (ship.requestType === 'ais') {
          setZoomToShip(ship);
          setTimeout(() => {
            setIsFocused(false);
          }, 300);
        } else if (ship.requestType === 'no-recent-ais') {
          setZoomToShip(ship);
          toast({
            title: messages.genericToastTitleWarning,
            description: messages.mapShipPortSearchNoRecentAisFoundWarning,
            variant: 'warning',
          });
          setTimeout(() => {
            setIsFocused(false);
          }, 300);
        } else if (ship.requestType === 'ship-db') {
          setSearchString(ship.name);
          // On mobile: only zoom, don't select vessel
          if (!isMobile) {
            setTargetInfoDetailsList([
              {
                extractedTargetInfo: [ship.shipDbResult],
                extractedTargetInfoType: TargetTypeEnum.ExtendedVessel,
              },
            ]);
          }
          toast({
            title: messages.genericToastTitleWarning,
            description: messages.mapShipPortSearchShipDBFoundWarning,
            variant: 'warning',
          });
          setTimeout(() => {
            setIsFocused(false);
          }, 300);
        } else {
          if (hasShipDbPermissions && ship.mmsi) {
            onSearchShipDbByMmsi(Number(ship.mmsi));
          } else {
            let description;
            if (!hasShipDbPermissions && ship.mmsi) {
              description = messages.mapShipPortSearchMmsiPresentNoShipDB;
            } else if (!hasShipDbPermissions && !ship.mmsi) {
              description = messages.mapShipPortSearchNoPermissionsWarning;
            } else {
              // hasShipDbPermissions && !ship.mmsi
              description = messages.mapShipPortSearchNoMmsiFoundWarning;
            }

            toast({
              title: messages.genericToastTitleWarning,
              description,
              variant: 'warning',
            });

            setTimeout(() => {
              setIsFocused(false);
            }, 300);
          }
        }
      },
      [
        setZoomToShip,
        toast,
        messages,
        setTargetInfoDetailsList,
        hasShipDbPermissions,
        onSearchShipDbByMmsi,
        isMobile,
      ],
    );

    useEffect(() => {
      if (inputRef.current && isFocused) {
        // Add a small timeout to ensure the Popover has rendered
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 50);
      }
    }, [isFocused]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const popoverContent = document.querySelector('.popover-content');
        const popoverTrigger = document.querySelector('.popover-trigger');
        if (
          popoverTrigger &&
          !popoverTrigger.contains(event.target as Node) &&
          popoverContent &&
          !popoverContent.contains(event.target as Node)
        ) {
          setIsFocused(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    return (
      <Popover open={isFocused}>
        <ShipPortSearchInput
          className={className}
          searchString={searchString}
          setSearchString={setSearchString}
          setIsFocused={setIsFocused}
          inputRef={inputRef}
          searchType={searchType}
          setSearchType={setSearchType}
        />
        <PopoverContent autoFocus={false} className="w-80 popover-content">
          {/*{searchType === 'ship' && (*/}
          {/*  <SearchExtendedVesselInfo*/}
          {/*    searchString={searchString}*/}
          {/*    setIsFocused={setIsFocused}*/}
          {/*  />*/}
          {/*)}*/}
          {/*{searchType === 'ship' && <Separator />}*/}
          {searchType === 'ship' && (
            <AISShipSearch
              searchString={searchString}
              onClick={onAISShipClick}
            />
          )}
          {searchType === 'port' && (
            <PortSearch
              searchString={searchString}
              setSearchString={setSearchString}
              setIsFocused={setIsFocused}
            />
          )}
        </PopoverContent>
      </Popover>
    );
  },
);

ShipPortSearch.displayName = 'ShipPortSearch';
